import type { BrandConfig } from '../constants/brands/types';

/**
 * Spec 029: a brand's home page settings. A null field means the built-in
 * default (`brand.copy` here, `AppBrand.homeCopy` in the app, the standard
 * banner overlay). Same rules as the backend `brands/home-page-settings.ts`
 * and Flutter `home_page_settings.dart`, checked against
 * `__fixtures__/homePage.fixture.json`.
 */
export type HomePageSettings = {
  webTitle: string | null;
  webSubtitle: string | null;
  appTitle: string | null;
  appSubtitle: string | null;
  overlayColor: string | null;
  overlayOpacity: number | null;
};

export const HOME_PAGE_TEXT_FIELDS = [
  'webTitle',
  'webSubtitle',
  'appTitle',
  'appSubtitle',
] as const;
export type HomePageTextField = (typeof HOME_PAGE_TEXT_FIELDS)[number];

export const HOME_PAGE_LIMITS: Record<HomePageTextField, number> = {
  webTitle: 29,
  webSubtitle: 135,
  appTitle: 15,
  appSubtitle: 60,
};

export const OVERLAY_OPACITY_MIN = 30;
export const OVERLAY_OPACITY_MAX = 95;
/** What the form shows for an unset overlay (the standard overlay's first stop). */
export const OVERLAY_FALLBACK_COLOR = '#18181B';
export const OVERLAY_FALLBACK_OPACITY = 90;

/** Today's overlay, kept exactly while the brand sets neither color nor strength. */
export const STANDARD_OVERLAY_CSS =
  'linear-gradient(135deg, rgba(24, 24, 27, 0.9) 0%, rgba(24, 24, 27, 0.75) 40%, rgba(201, 184, 150, 0.2) 100%)';

/** Same proportions as today's three stops (0.9, 0.75, 0.2). */
const OVERLAY_STOPS: ReadonlyArray<[number, number]> = [
  [0, 1],
  [40, 0.83],
  [100, 0.22],
];

export const EMPTY_HOME_PAGE: HomePageSettings = {
  webTitle: null,
  webSubtitle: null,
  appTitle: null,
  appSubtitle: null,
  overlayColor: null,
  overlayOpacity: null,
};

export type HomeTextProblem = 'too_long' | 'control_characters';

// eslint-disable-next-line no-control-regex
const CONTROL_CHARACTERS = /[\u0000-\u001F\u007F]/;
const HEX_COLOR = /^#[0-9a-fA-F]{6}$/;

/** Whitespace runs become one space and the ends are trimmed; empty is null. */
export function normalizeHomeText(value: string | null | undefined): string | null {
  if (value == null) return null;
  const text = String(value).replace(/\s+/g, ' ').trim();
  return text || null;
}

/** Code points, so an emoji counts once. */
export function countChars(text: string | null | undefined): number {
  return text ? Array.from(text).length : 0;
}

export function homeTextProblem(
  field: HomePageTextField,
  value: string | null | undefined,
): HomeTextProblem | null {
  const text = normalizeHomeText(value);
  if (text == null) return null;
  if (CONTROL_CHARACTERS.test(text)) return 'control_characters';
  return countChars(text) > HOME_PAGE_LIMITS[field] ? 'too_long' : null;
}

export function isHexColor(value: string | null | undefined): boolean {
  return !!value && HEX_COLOR.test(value.trim());
}

export function isValidOverlayOpacity(value: number | null | undefined): boolean {
  return (
    value != null &&
    Number.isInteger(value) &&
    value >= OVERLAY_OPACITY_MIN &&
    value <= OVERLAY_OPACITY_MAX
  );
}

function rgbOf(hex: string): [number, number, number] {
  const h = hex.trim().replace('#', '');
  return [0, 2, 4].map(i => parseInt(h.slice(i, i + 2), 16)) as [number, number, number];
}

/** The banner overlay for a brand's settings (spec 029 FR-010). */
export function heroOverlayCss(color: string | null, opacity: number | null): string {
  const hasColor = isHexColor(color);
  const hasOpacity = isValidOverlayOpacity(opacity);
  if (!hasColor && !hasOpacity) return STANDARD_OVERLAY_CSS;
  const [r, g, b] = rgbOf(hasColor ? (color as string) : OVERLAY_FALLBACK_COLOR);
  const alpha = (hasOpacity ? (opacity as number) : OVERLAY_FALLBACK_OPACITY) / 100;
  const stops = OVERLAY_STOPS.map(
    ([at, factor]) => `rgba(${r}, ${g}, ${b}, ${Number((alpha * factor).toFixed(3))}) ${at}%`,
  );
  return `linear-gradient(135deg, ${stops.join(', ')})`;
}

function channel(value: number): number {
  const c = value / 255;
  return c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4);
}

/** WCAG contrast ratio of white text on `hex`. */
export function whiteContrast(hex: string): number {
  const [r, g, b] = rgbOf(hex);
  const luminance = 0.2126 * channel(r) + 0.7152 * channel(g) + 0.0722 * channel(b);
  return 1.05 / (luminance + 0.05);
}

/** A light color or a weak overlay may leave the white headline hard to read (a hint only). */
export function overlayReadabilityWarning(color: string | null, opacity: number | null): boolean {
  const c = isHexColor(color) ? (color as string) : OVERLAY_FALLBACK_COLOR;
  const o = opacity ?? OVERLAY_FALLBACK_OPACITY;
  return whiteContrast(c) < 4.5 || o < 50;
}

/** The app's built-in home words (Flutter `HomeCopy`), shown on the admin page. */
export const APP_HOME_DEFAULTS: Record<string, { appTitle: string; appSubtitle: string }> = {
  'autopilotee.com': {
    appTitle: 'Find your drive',
    appSubtitle: 'Book cars with premium convenience — compare prices and go.',
  },
  'lenvoy.co': {
    appTitle: 'Find your drive',
    appSubtitle: 'Vehicles for the way you work.',
  },
};

export type HomeWords = {
  webTitle: string;
  webSubtitle: string;
  appTitle: string;
  appSubtitle: string;
};

/** Built-in words for a brand: the web skin's copy and the app's `HomeCopy`. */
export function defaultHomeWords(brand: Pick<BrandConfig, 'brandSite' | 'copy'>): HomeWords {
  const app = APP_HOME_DEFAULTS[brand.brandSite] ?? APP_HOME_DEFAULTS['autopilotee.com'];
  return {
    webTitle: brand.copy.heroHeadline,
    webSubtitle: brand.copy.heroSubcopy,
    appTitle: app.appTitle,
    appSubtitle: app.appSubtitle,
  };
}

/** The words a page shows: the brand's setting, else the built-in default. */
export function effectiveHomeWords(
  brand: Pick<BrandConfig, 'brandSite' | 'copy'>,
  settings: HomePageSettings | null | undefined,
): HomeWords {
  const defaults = defaultHomeWords(brand);
  const s = settings ?? EMPTY_HOME_PAGE;
  return {
    webTitle: s.webTitle ?? defaults.webTitle,
    webSubtitle: s.webSubtitle ?? defaults.webSubtitle,
    appTitle: s.appTitle ?? defaults.appTitle,
    appSubtitle: s.appSubtitle ?? defaults.appSubtitle,
  };
}

/**
 * Reads an answer or a cached value (FR-012): a field that is not text, is too
 * long, or is not a valid color or strength is dropped (the default shows).
 */
export function sanitizeHomePage(raw: unknown): HomePageSettings {
  const v = (raw && typeof raw === 'object' ? raw : {}) as Record<string, unknown>;
  const out: HomePageSettings = { ...EMPTY_HOME_PAGE };
  for (const field of HOME_PAGE_TEXT_FIELDS) {
    const value = v[field];
    if (typeof value !== 'string') continue;
    const text = normalizeHomeText(value);
    if (text != null && homeTextProblem(field, text) == null) out[field] = text;
  }
  if (typeof v.overlayColor === 'string' && isHexColor(v.overlayColor)) {
    out.overlayColor = v.overlayColor.trim().toUpperCase();
  }
  if (typeof v.overlayOpacity === 'number' && isValidOverlayOpacity(v.overlayOpacity)) {
    out.overlayOpacity = v.overlayOpacity;
  }
  return out;
}
